import { useCallback, useState } from 'react';

import { type ConfirmCommand } from '../../../shared/components/ConfirmOption';
import { type InvertedLocalize } from '../../../shared/utils/localization';
import { deleteClient } from '../data-sources';
import { type Client } from '../types';

interface UseDeleteWithCaptchaResult {
  /** Build and set the confirm request for the given client */
  requestDelete: (client: Client) => void;
  /** Current confirm request (pass to ConfirmOption) */
  confirmRequest: ConfirmCommand | undefined;
  /** Clear the confirm request (pass to ConfirmOption clearRequest) */
  clearRequest: () => void;
  /** Toast message set after successful delete (auto-managed by consumer) */
  successMessage: string | null;
  /** Clear the success message */
  clearSuccessMessage: () => void;
  /** Error message set after a failed delete */
  errorMessage: string | null;
  /** Clear the error message */
  clearErrorMessage: () => void;
}

/**
 * Encapsulates the captcha-based delete flow:
 * builds a ConfirmCommand, calls deleteClient on confirm, sets a success message,
 * and triggers refetch.
 *
 * @param refetch - callback to refresh the list after a successful delete
 * @param localize - localization function (useInvertedLocalize)
 */
export function useDeleteWithCaptcha(
  refetch: () => void,
  localize: InvertedLocalize
): UseDeleteWithCaptchaResult {
  const [confirmRequest, setConfirmRequest] = useState<
    ConfirmCommand | undefined
  >(undefined);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const clearRequest = useCallback(() => setConfirmRequest(undefined), []);
  const clearSuccessMessage = useCallback(() => setSuccessMessage(null), []);
  const clearErrorMessage = useCallback(() => setErrorMessage(null), []);

  const requestDelete = useCallback(
    (client: Client) => {
      setConfirmRequest({
        title: localize('Delete Client'),
        description: localize('Are you sure you want to delete {clientName}?', {
          clientName: client.clientName,
        }),
        captcha: client.clientName,
        onConfirm: () => {
          deleteClient(client.clientId)
            .then(() => {
              setSuccessMessage(localize('Client deleted.'));
              refetch();
            })
            .catch(() => {
              setErrorMessage(
                localize('Failed to delete client. Please try again.')
              );
            });
        },
      });
    },
    [localize, refetch]
  );

  return {
    requestDelete,
    confirmRequest,
    clearRequest,
    successMessage,
    clearSuccessMessage,
    errorMessage,
    clearErrorMessage,
  };
}
